import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { throttle } from 'lodash';

import {
  authTrigger, isUserLoggedIn, setAuthPathAttribute,
} from '@alumni_v2/utils/helpers';
import { downloadReport } from '@alumni_v2/api/placementReport';
import { GTMTracking } from '@common/ui/general';
import { PLACEMENT_BANNER_LINK } from '@alumni_v2/utils/constants';
import {
  requestParamsSelector,
} from '@alumni_v2/selectors/reviews';
import { useMediaQuery } from '@common/hooks';

import halfStar from '@alumni_v2/images/svg/half-star.svg';
import officeBag from '@alumni_v2/images/svg/officeBag.svg';
import placementIcon from '@alumni_v2/images/webp/placement-icon.webp';
import rupeeIcon from '@alumni_v2/images/svg/rupee.svg';
import star from '@alumni_v2/images/svg/star.svg';
import thumbIcon from '@alumni_v2/images/svg/thumb.svg';

import styles from './TopBanner.module.scss';
import TopBannerRevamp from './TopBannerRevamp';
import PlacementBannerRevamp from './placementBannerRevamp';

const TopBanner = () => {
  const { mobile } = useMediaQuery();
  const requestParams = useSelector(requestParamsSelector) || {};
  const showPlacementBanner = window.__INITIAL_STATE__.showPlacementBanner
    === 'true';

  const sendDataOnDownloadReport = useCallback(async () => {
    await downloadReport({ source: 'alumni_page' });
  }, []);

  const throttledDownloadReport = throttle(() => {
    sendDataOnDownloadReport();
  }, 5 * 60000, { leading: true });

  const openPlacemnetBrochure = useCallback(() => {
    if (isUserLoggedIn()) {
      throttledDownloadReport();
      window.open(PLACEMENT_BANNER_LINK, '_blank');
    } else {
      setAuthPathAttribute(PLACEMENT_BANNER_LINK);
      authTrigger();
    }
  }, [throttledDownloadReport]);

  if (requestParams.search
    || requestParams.quickFilters.length
    || Object.keys(requestParams.advancedFilters).length
  ) {
    return (
      <PlacementBannerRevamp />
    );
  }

  if (showPlacementBanner) {
    return <TopBannerRevamp />;
  }

  return (
    <>
      <div className={styles.bannerContainer}>
        <div className={styles.container}>
          <div className={classNames(
            styles.contentContainer, styles.expand,
            styles.bottomFlatContainer,
          )}
          >
            <div className={styles.leftContainer}>
              <div className={styles.upperText}>
                Find & Connect with
              </div>
              <div className={styles.lowerText}>
                Scaler Alumni
              </div>
            </div>

            <div className={styles.rightContainer}>
              <div className={styles.iconBox}>
                <div className={styles.icon}>
                  <img src={thumbIcon} alt="icon" />
                </div>
                <div className={styles.textBox}>
                  <div className={styles.headText}>1.7 Cr</div>
                  <div className={styles.subText}>Highest Package</div>
                </div>
              </div>

              <div className={styles.iconBox}>
                <div className={styles.icon}>
                  <img src={rupeeIcon} alt="icon" />
                </div>
                <div className={styles.textBox}>
                  <div className={styles.headText}>126%</div>
                  <div className={styles.subText}>Average Hike</div>
                </div>
              </div>

              <div className={styles.iconBox}>
                <div className={styles.icon}>
                  <img src={officeBag} alt="icon" />
                </div>
                <div className={styles.textBox}>
                  <div className={styles.headText}>93.5%</div>
                  <div className={styles.subText}>Placement Rate</div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={classNames(styles.placementBanner, styles.expand)}
          >
            <div className={styles.placementLeftContainer}>
              <img
                src={placementIcon}
                alt="placement-icon"
                className={styles.mobileIcon}
              />
              <div className={styles.placementContent}>
                2024 Scaler Placement Assessment Report
                {

                  mobile && (
                    <GTMTracking
                      component="button"
                      gtmEventType="cta"
                      gtmEventAction="click"
                      gtmEventResult="download_audit_report"
                      gtmEventCategory="alumni_directory"
                      gtmEventData={{ is_logged_in: isUserLoggedIn() }}
                      className={styles.placementRightContainer}
                      onClick={openPlacemnetBrochure}
                      role="button"
                      tabIndex={0}
                    >
                      View Now
                    </GTMTracking>
                  )
                }
              </div>
            </div>
            {
              !mobile && (
                <GTMTracking
                  component="button"
                  gtmEventType="cta"
                  gtmEventAction="click"
                  gtmEventResult="download_audit_report"
                  gtmEventCategory="alumni_directory"
                  gtmEventData={{ is_logged_in: isUserLoggedIn() }}
                  className={styles.placementRightContainer}
                  onClick={openPlacemnetBrochure}
                  role="button"
                  tabIndex={0}
                >
                  View Now
                </GTMTracking>
              )
            }
          </div>
        </div>
        <div className={styles.contentContainer}>
          <div className={styles.ratingContainer}>
            <div className={styles.headText}>
              Average Rating
            </div>
            <div className={styles.ratingContent}>
              <div className={styles.ratingHeader}>
                4.5
              </div>
              <div className={styles.ratingBox}>
                <div className={styles.ratingStars}>
                  <img src={star} alt="star" className={styles.ratingStar} />
                  <img src={star} alt="star" className={styles.ratingStar} />
                  <img src={star} alt="star" className={styles.ratingStar} />
                  <img src={star} alt="star" className={styles.ratingStar} />
                  <img
                    src={halfStar}
                    alt="empty star"
                    className={styles.ratingStar}
                  />
                </div>
                <div className={styles.ratingSubHeader}>
                  Based on 4,292 ratings
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>

  );
};

export default TopBanner;
